import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet, ArrowLeft, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { getCurrentQuarter } from "../lib/quarterUtils";

const fmtSigned = (v) => `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}`;

const PeriodControls = ({ year, setYear, quarter, setQuarter, period, setPeriod, currentYear }) => (
  <div className="flex flex-wrap items-center justify-center gap-2" data-testid="rr-period-controls">
    <select value={year} onChange={(e) => setYear(Number(e.target.value))}
      className="h-9 rounded-md border border-slate-600 bg-slate-800 px-2 text-sm text-slate-200" data-testid="rr-year-select">
      {[currentYear - 1, currentYear, currentYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
    <select value={quarter} onChange={(e) => setQuarter(e.target.value)}
      className="h-9 rounded-md border border-slate-600 bg-slate-800 px-2 text-sm text-slate-200" data-testid="rr-quarter-select">
      {["Q1", "Q2", "Q3", "Q4"].map((q) => <option key={q} value={q}>{q}</option>)}
    </select>
    <div className="inline-flex rounded-md border border-slate-600 overflow-hidden" role="group">
      {[["qtd", `${quarter} to date`], ["all", "All-time clicks"]].map(([val, label]) => (
        <button key={val} type="button" onClick={() => setPeriod(val)}
          className={`h-9 px-3 text-xs sm:text-sm font-semibold transition-colors ${period === val ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
          data-testid={`rr-period-${val}`}>
          {label}
        </button>
      ))}
    </div>
  </div>
);

export default function QRReviewReport() {
  const current = getCurrentQuarter();
  const [quarter, setQuarter] = useState(current.quarter);
  const [year, setYear] = useState(current.year);
  const [period, setPeriod] = useState("qtd");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/qr/review-report?quarter=${quarter}&year=${year}&period=${period}`);
      setReport(res.data);
    } catch (e) {
      setReport(null);
      toast.error(e?.response?.data?.detail || "Failed to load report");
    }
    setLoading(false);
  }, [quarter, year, period]);

  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const res = await api.get(`/qr/review-report/${kind}?quarter=${quarter}&year=${year}&period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr_review_report_${quarter}_${year}_${period === "qtd" ? "QTD" : "all_time"}.${kind === "csv" ? "csv" : "png"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    setDownloading(null);
  };

  const cols = report?.columns || [];
  const rows = report?.rows || [];
  const t = report?.totals;

  return (
    <div className="min-h-screen bg-slate-900 p-4 sm:p-6 lg:p-8" data-testid="qr-review-report-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <Link to="/qr/leaderboard" className="inline-flex items-center text-xs text-slate-400 hover:text-slate-200 mb-2" data-testid="rr-back-link">
              <ArrowLeft className="w-3 h-3 mr-1" /> QR Leaderboard
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-violet-400" /> QR &amp; Review Report
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Review Tracker mentions, QR clicks, Customer Voice and the review points each person adds to their ranking score.
              {report?.snapshot?.name && <span className="text-slate-500"> · Source: {report.snapshot.name}</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => download("csv")} disabled={!rows.length || !!downloading}
              className="border-slate-600 text-slate-200 hover:bg-slate-800 text-xs sm:text-sm" data-testid="rr-download-csv">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> {downloading === "csv" ? "Preparing…" : "CSV"}
            </Button>
            <Button onClick={() => download("slide")} disabled={!rows.length || !!downloading}
              className="bg-violet-600 hover:bg-violet-700 text-white text-xs sm:text-sm" data-testid="rr-download-slide">
              <Download className="w-4 h-4 mr-2" /> {downloading === "slide" ? "Rendering…" : "Download Slide (PNG)"}
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <PeriodControls year={year} setYear={setYear} quarter={quarter} setQuarter={setQuarter}
            period={period} setPeriod={setPeriod} currentYear={current.year} />
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-800/60 overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400">Loading…</div>
          ) : !rows.length ? (
            <div className="p-10 text-center text-slate-400" data-testid="rr-empty">No snapshot data for {quarter} {year}.</div>
          ) : (
            <table className="w-full text-sm" data-testid="rr-table">
              <thead className="bg-slate-700/70 text-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  {cols.map((c) => (
                    <th key={c.key} className={`px-3 py-2 ${c.key === "name" ? "text-left" : "text-center"} whitespace-nowrap`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || r.name} className={`${i % 2 ? "bg-slate-900/30" : ""} border-t border-slate-700/50`} data-testid={`rr-row-${r.rank}`}>
                    <td className="px-3 py-2 text-slate-400">{r.rank}</td>
                    <td className="px-3 py-2 font-semibold text-white">
                      {r.name}
                      {r.tier_label && <span className="ml-2 text-[10px] uppercase text-slate-500">{r.tier_label}</span>}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-200">{r.rt_reviews}</td>
                    <td className="px-3 py-2 text-center text-slate-200">{r.qr_clicks}</td>
                    <td className="px-3 py-2 text-center text-slate-200">{r.cv_reviews ? `${Math.round(r.nps_pct)}%` : "—"}</td>
                    <td className="px-3 py-2 text-center text-slate-200">{r.cv_reviews}</td>
                    <td className={`px-3 py-2 text-center ${r.detractors > 0 ? "text-red-400 font-semibold" : "text-slate-200"}`}>{r.detractors}</td>
                    <td className="px-3 py-2 text-center text-emerald-300">{fmtSigned(r.rt_bonus)}</td>
                    <td className={`px-3 py-2 text-center ${r.cv_bonus < 0 ? "text-red-400" : "text-emerald-300"}`}>{fmtSigned(r.cv_bonus)}</td>
                    <td className={`px-3 py-2 text-center font-bold ${i < 3 ? "text-amber-300" : "text-white"}`}>{Number(r.total_review_pts).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
              {t && (
                <tfoot className="bg-slate-700/70 text-white font-semibold" data-testid="rr-totals">
                  <tr>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-violet-300">TEAM</td>
                    <td className="px-3 py-2 text-center">{t.rt_reviews}</td>
                    <td className="px-3 py-2 text-center">{t.qr_clicks}</td>
                    <td className="px-3 py-2 text-center">{Math.round(t.nps_pct)}%</td>
                    <td className="px-3 py-2 text-center">{t.cv_reviews}</td>
                    <td className="px-3 py-2 text-center">{t.detractors}</td>
                    <td className="px-3 py-2 text-center">{fmtSigned(t.rt_bonus)}</td>
                    <td className="px-3 py-2 text-center">{fmtSigned(t.cv_bonus)}</td>
                    <td className="px-3 py-2 text-center">{Number(t.total_review_pts).toFixed(1)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Total Review Pts = RT Bonus + CV Bonus (promoters +1 / detractors −2) — the combined review contribution to the rankings score. NPS % is weighted by responses in the team row.
        </p>
      </div>
    </div>
  );
}
